import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CUSTOMER_IMPORT_FIELDS,
  customerImportField,
  matchColumns,
  type ColumnMapping,
  type CustomerImportKey,
} from '@shared/customer-import.ts';
import { CUSTOMER_IMPORT_BATCH_MAX, type ImportDuplicateStrategy } from '@shared/schemas/customers.ts';
import { customerApi, type CustomerImportResult, type ImportRowResult } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, Select } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import {
  buildPreviewRows,
  chunk,
  columnCount,
  columnLabels,
  ImportFileError,
  importableRows,
  looksLikeHeaderRow,
  problemRows,
  MAX_IMPORT_ROWS,
  readImportFile,
  readPastedRows,
  type ImportSource,
  type PreviewRow,
  type SourceRow,
} from '../lib/customer-import-file.ts';
import { isSamePhoneNumber, normalizePhoneToE164 } from '@shared/phone.ts';

/**
 * Bringing an existing customer list into the app.
 *
 * The screen is built around one belief: the file is fine as it is. Somebody
 * migrating off another system has a spreadsheet they did not design and cannot
 * re-key, so nothing here asks them to rename a column, split a name field or
 * save as a different format. It reads what they have, says what it thinks each
 * column is, and lets them correct it — and it says how many of these people
 * are already on file *before* anything is written, because "did that just
 * create four hundred duplicates?" is the question an import has to answer in
 * advance rather than afterwards.
 */

type Step = 'source' | 'map' | 'review' | 'done';

/**
 * Above this, the "which of these already exist?" check is offered rather than
 * run on arrival. It is a round trip per hundred rows, and on a list of
 * thousands that is a wait nobody asked for while they are still deciding
 * whether the columns look right.
 */
const AUTO_CHECK_ROW_LIMIT = 500;

/** What one pass of `send` did, and whether it got to the end of the list. */
interface SendOutcome {
  /** A batch failed, so the rows after it were never attempted. */
  stopped: boolean;
  unattempted: number;
  result: CustomerImportResult;
  /** The duplicate strategy this pass ran under, for spotting a stale answer. */
  strategy: ImportDuplicateStrategy;
}

export default function CustomerImportPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('customers.import.documentTitle'));
  const { can, company } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('source');
  const [source, setSource] = useState<ImportSource | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<ColumnMapping>([]);
  const [guessed, setGuessed] = useState<ReadonlySet<number>>(new Set());
  const [strategy, setStrategy] = useState<ImportDuplicateStrategy>('skip');
  const [addProperty, setAddProperty] = useState(true);
  const [checked, setChecked] = useState<CustomerImportResult | null>(null);
  const [outcome, setOutcome] = useState<CustomerImportResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Rows the run never reached, when a batch failed partway. */
  const [stopped, setStopped] = useState<number | null>(null);

  const countryCode = company?.countryCode ?? 'US';
  /**
   * The strategy as of now, readable from inside a request that started under
   * an earlier one. State would be the value captured when the call was made,
   * which is exactly the stale value this guards against.
   */
  const strategyRef = useRef(strategy);
  strategyRef.current = strategy;

  const sheet = source?.sheets[sheetIndex];
  const rows = useMemo(() => sheet?.rows ?? [], [sheet]);
  const count = useMemo(() => columnCount(rows), [rows]);
  const labels = useMemo(() => columnLabels(rows, hasHeader, count), [rows, hasHeader, count]);
  const unreadableId = t('customers.import.unreadableId');
  const preview = useMemo(
    () => buildPreviewRows(rows, mapping, hasHeader, unreadableId),
    [rows, mapping, hasHeader, unreadableId],
  );
  const importable = useMemo(() => importableRows(preview), [preview]);

  /** Re-guesses the mapping — on a new file, another sheet, or a header toggle. */
  const autoMap = useCallback((sheetRows: readonly SourceRow[], header: boolean) => {
    const width = columnCount(sheetRows);
    if (!header) {
      setMapping(Array.from({ length: width }, () => null));
      setGuessed(new Set());
      return;
    }
    const matches = matchColumns(columnLabels(sheetRows, true, width));
    setMapping(matches.map((match) => match?.key ?? null));
    setGuessed(new Set(matches.flatMap((match, index) => (match?.confidence === 'likely' ? [index] : []))));
  }, []);

  const accept = useCallback(
    (next: ImportSource) => {
      const first = next.sheets[0];
      const header = looksLikeHeaderRow(first?.rows[0]);
      setSource(next);
      setSheetIndex(0);
      setHasHeader(header);
      autoMap(first?.rows ?? [], header);
      setChecked(null);
      setOutcome(null);
      setError(null);
      setStep('map');
    },
    [autoMap],
  );

  const readFile = useCallback(
    async (file: File) => {
      try {
        accept(await readImportFile(file));
      } catch (caught) {
        setSource(null);
        setError(
          caught instanceof ImportFileError
            ? t(`customers.import.fileError.${caught.code}`)
            : (caught as Error).message,
        );
      }
    },
    [accept, t],
  );

  const readPaste = useCallback(
    (text: string) => {
      try {
        accept(readPastedRows(text));
      } catch (caught) {
        setError(caught instanceof ImportFileError ? t(`customers.import.fileError.${caught.code}`) : String(caught));
      }
    },
    [accept, t],
  );

  /**
   * Sends the rows, a batch at a time.
   *
   * The same loop does the dry run and the real one, so what the review step
   * promised and what the import does cannot drift apart. A batch that fails
   * stops the run and keeps what came before it: the counts reported are always
   * what actually happened, never what was attempted.
   */
  const send = useCallback(
    async (dryRun: boolean): Promise<SendOutcome> => {
      const batches = chunk(importable, CUSTOMER_IMPORT_BATCH_MAX);
      const merged: CustomerImportResult = { created: 0, updated: 0, skipped: 0, failed: 0, dryRun, results: [] };
      let sent = 0;
      setError(null);
      setProgress({ done: 0, total: batches.length });

      try {
        for (const [index, batch] of batches.entries()) {
          const result = await customerApi.import({
            rows: batch.map((row) => ({ line: row.line, id: row.id, values: row.values })),
            duplicateStrategy: strategy,
            addBillingAddressAsProperty: addProperty,
            dryRun,
          });
          merged.created += result.created;
          merged.updated += result.updated;
          merged.skipped += result.skipped;
          merged.failed += result.failed;
          merged.results.push(...result.results);
          sent += batch.length;
          setProgress({ done: index + 1, total: batches.length });
        }
      } catch (caught) {
        setError((caught as Error).message);
        setProgress(null);
        // What did land still has to be reported, or a retry duplicates it —
        // and it has to be reported *as* a run that stopped, because a summary
        // that reads like a finished import is how somebody imports the same
        // list twice.
        return { stopped: true, unattempted: importable.length - sent, result: merged, strategy };
      }

      setProgress(null);
      return { stopped: false, unattempted: 0, result: merged, strategy };
    },
    [importable, strategy, addProperty],
  );

  /** The rows the browser already knows will not save, folded in as failures. */
  const localResults = useMemo(
    (): ImportRowResult[] =>
      preview
        .filter((row) => row.empty || Object.keys(row.fieldErrors).length > 0)
        .map((row) =>
          row.empty
            ? { line: row.line, status: 'skipped' as const, reason: 'empty' as const }
            : {
                line: row.line,
                status: 'failed' as const,
                reason: 'invalid' as const,
                fields: row.fieldErrors,
                message: Object.values(row.fieldErrors)[0],
              },
        ),
    [preview],
  );

  const check = useCallback(async () => {
    const outcome = await send(true);
    if (outcome.stopped) return;
    // The selector stays live while a multi-batch check runs, so by the time
    // this answer arrives it may be about a strategy nobody is choosing any
    // more. Showing "12 already on file, left alone" beside an import set to
    // update them would be worse than showing nothing.
    if (outcome.strategy !== strategyRef.current) return;
    setChecked(withInFileDuplicates(outcome.result, importable, outcome.strategy, countryCode));
  }, [send, importable, countryCode]);

  const runImport = useCallback(async () => {
    const outcome = await send(false);
    setOutcome(mergeLocal(outcome.result, localResults));
    setStopped(outcome.stopped ? outcome.unattempted : null);
    setStep('done');
    // Every customer list on screen is now out of date.
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
  }, [send, localResults, queryClient]);

  // The check is about the rows as currently mapped. Change the mapping, the
  // strategy or the sheet and the old answer is about a different import.
  useEffect(() => {
    setChecked(null);
  }, [mapping, hasHeader, sheetIndex, strategy]);

  if (!can('customers:write')) {
    return (
      <WorkPage title={t('customers.import.title')}>
        <WorkEmpty title={t('customers.import.noPermission')} />
      </WorkPage>
    );
  }

  return (
    <WorkPage
      title={t('customers.import.title')}
      description={t('customers.import.description')}
      actions={
        <Link className="btn btn-secondary" to="/customers">
          {t('customers.import.backToCustomers')}
        </Link>
      }
    >
      <StepTrail step={step} />

      {error ? (
        <Alert tone="error" title={t('customers.import.problemTitle')}>
          {error}
        </Alert>
      ) : null}

      {step === 'source' ? <SourceStep onFile={readFile} onPaste={readPaste} /> : null}

      {step === 'map' && source && sheet ? (
        <MapStep
          source={source}
          sheetIndex={sheetIndex}
          onSheetChange={(index) => {
            const next = source.sheets[index];
            const header = looksLikeHeaderRow(next?.rows[0]);
            setSheetIndex(index);
            setHasHeader(header);
            autoMap(next?.rows ?? [], header);
          }}
          rows={rows}
          labels={labels}
          mapping={mapping}
          guessed={guessed}
          hasHeader={hasHeader}
          onHeaderChange={(value) => {
            setHasHeader(value);
            autoMap(rows, value);
          }}
          onMappingChange={(column, key) => {
            setMapping((current) => {
              const next = [...current];
              // A field belongs to one column. Pointing a second column at it
              // takes it, rather than leaving two columns quietly fighting over
              // which one the row ends up with.
              if (key) {
                for (let index = 0; index < next.length; index += 1) if (next[index] === key) next[index] = null;
              }
              next[column] = key;
              return next;
            });
            setGuessed((current) => {
              const next = new Set(current);
              next.delete(column);
              return next;
            });
          }}
          onBack={() => {
            setSource(null);
            setStep('source');
          }}
          onContinue={() => setStep('review')}
        />
      ) : null}

      {step === 'review' ? (
        <ReviewStep
          preview={preview}
          importable={importable}
          truncated={source?.truncated === true}
          strategy={strategy}
          onStrategyChange={setStrategy}
          addProperty={addProperty}
          onAddPropertyChange={setAddProperty}
          checked={checked}
          onCheck={check}
          progress={progress}
          onBack={() => setStep('map')}
          onImport={runImport}
        />
      ) : null}

      {step === 'done' && outcome ? (
        <DoneStep
          outcome={outcome}
          stopped={stopped}
          preview={preview}
          labels={labels}
          hasHeader={hasHeader}
          mapping={mapping}
          onViewCustomers={() => navigate('/customers')}
          onImportAnother={() => {
            setSource(null);
            setOutcome(null);
            setChecked(null);
            setStep('source');
          }}
        />
      ) : null}
    </WorkPage>
  );
}

/** Which of the four steps we are on, as a plain ordered list. */
function StepTrail({ step }: { step: Step }) {
  const { t } = useTranslation();
  const steps: Step[] = ['source', 'map', 'review', 'done'];
  const current = steps.indexOf(step);

  return (
    <ol className="mb-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
      {steps.map((name, index) => (
        <li
          key={name}
          aria-current={index === current ? 'step' : undefined}
          className={
            index === current ? 'font-semibold text-[color:var(--color-brand)]' : 'text-[color:var(--color-muted)]'
          }
        >
          <span aria-hidden="true">{index + 1}. </span>
          {t(`customers.import.step.${name}`)}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — the file, or the clipboard
// ---------------------------------------------------------------------------

function SourceStep({ onFile, onPaste }: { onFile: (file: File) => void; onPaste: (text: string) => void }) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Pasting is the fastest way in, so it works anywhere on the step rather than
  // only inside the box — except while the caret is in a field, where the paste
  // plainly belongs to that field.
  useEffect(() => {
    const onDocumentPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      const text = event.clipboardData?.getData('text/plain');
      if (!text?.trim()) return;
      event.preventDefault();
      onPaste(text);
    };
    document.addEventListener('paste', onDocumentPaste);
    return () => document.removeEventListener('paste', onDocumentPaste);
  }, [onPaste]);

  return (
    <>
      <section
        className={`card mb-6 border-2 border-dashed text-center ${
          dragging ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]' : ''
        }`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file) onFile(file);
        }}
      >
        <p className="mb-2 text-lg font-semibold">{t('customers.import.dropTitle')}</p>
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('customers.import.dropHint')}</p>
        <input
          ref={fileInput}
          type="file"
          className="sr-only"
          accept=".csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onFile(file);
            // Cleared so choosing the same file twice still fires a change.
            event.target.value = '';
          }}
        />
        <Button variant="primary" onClick={() => fileInput.current?.click()}>
          {t('customers.import.chooseFile')}
        </Button>
      </section>

      <section className="card mb-6">
        <h2 className="mb-1 text-lg font-semibold">{t('customers.import.pasteTitle')}</h2>
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('customers.import.pasteHint')}</p>
        <textarea
          className="field-control font-mono text-sm"
          rows={6}
          aria-label={t('customers.import.pasteLabel')}
          placeholder={t('customers.import.pastePlaceholder')}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text/plain');
            if (!text.trim()) return;
            event.preventDefault();
            onPaste(text);
          }}
          onChange={(event) => {
            // Typed or dropped in rather than pasted — same thing once there is
            // more than one line to read.
            const text = event.target.value;
            if (text.includes('\n')) onPaste(text);
          }}
        />
      </section>

      <section className="card">
        <h2 className="mb-1 text-lg font-semibold">{t('customers.import.templateTitle')}</h2>
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('customers.import.templateHint')}</p>
        <Button onClick={downloadTemplate}>{t('customers.import.downloadTemplate')}</Button>
      </section>
    </>
  );
}

/** A starter file with every column this understands, and one example row. */
function downloadTemplate(): void {
  const fields = CUSTOMER_IMPORT_FIELDS.filter((field) => field.key !== 'id' && field.key !== 'fullName');
  const example: Partial<Record<CustomerImportKey, string>> = {
    firstName: 'Dana',
    lastName: 'Okafor',
    companyName: '',
    email: 'dana@example.com',
    phone: '(613) 555-0142',
    billingAddressLine1: '14 Rue Cler',
    billingCity: 'Ottawa',
    billingState: 'ON',
    billingPostalCode: 'K1A 0B1',
    billingCountryCode: 'CA',
    invoiceTermsDays: '30',
    consolidatedBilling: 'no',
    smsMarketingEnabled: 'no',
    emailMarketingEnabled: 'yes',
  };

  const csv = [
    fields.map((field) => field.label).join(','),
    fields.map((field) => quoteCsv(example[field.key] ?? '')).join(','),
  ].join('\r\n');

  downloadFile('customer-import-template.csv', 'text/csv;charset=utf-8', `${csv}\r\n`);
}

// ---------------------------------------------------------------------------
// Step 2 — what each column is
// ---------------------------------------------------------------------------

function MapStep({
  source,
  sheetIndex,
  onSheetChange,
  rows,
  labels,
  mapping,
  guessed,
  hasHeader,
  onHeaderChange,
  onMappingChange,
  onBack,
  onContinue,
}: {
  source: ImportSource;
  sheetIndex: number;
  onSheetChange: (index: number) => void;
  rows: readonly SourceRow[];
  labels: readonly string[];
  mapping: ColumnMapping;
  guessed: ReadonlySet<number>;
  hasHeader: boolean;
  onHeaderChange: (value: boolean) => void;
  onMappingChange: (column: number, key: CustomerImportKey | null) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const body = hasHeader ? rows.slice(1) : rows;
  const mapped = mapping.filter(Boolean).length;
  // Without one of these there is nothing to call anybody, and every row will
  // be refused. Worth saying now rather than in the preview.
  const hasName = mapping.some(
    (key) => key === 'fullName' || key === 'firstName' || key === 'lastName' || key === 'companyName',
  );

  const options = useMemo(
    () => [
      { value: '', label: t('customers.import.doNotImport') },
      ...CUSTOMER_IMPORT_FIELDS.map((field) => ({
        value: field.key,
        label: t(`customers.import.field.${field.key}`),
      })),
    ],
    [t],
  );

  return (
    <>
      <section className="card mb-6">
        <p className="mb-3">{t('customers.import.mapSummary', { mapped, total: mapping.length, rows: body.length })}</p>

        {source.sheets.length > 1 ? (
          <Select
            label={t('customers.import.sheetLabel')}
            hint={t('customers.import.sheetHint')}
            value={String(sheetIndex)}
            onChange={(event) => onSheetChange(Number(event.target.value))}
            options={source.sheets.map((item, index) => ({
              value: String(index),
              label: t('customers.import.sheetOption', { name: item.name, count: item.rows.length }),
            }))}
          />
        ) : null}

        <Checkbox
          label={t('customers.import.hasHeaderLabel')}
          hint={t('customers.import.hasHeaderHint')}
          checked={hasHeader}
          onChange={(event) => onHeaderChange(event.target.checked)}
        />

        {!hasName ? (
          <Alert tone="warning" title={t('customers.import.noNameColumnTitle')}>
            {t('customers.import.noNameColumnBody')}
          </Alert>
        ) : null}
      </section>

      <TableScroll
        aria-label={t('customers.import.mapCaption')}
        className="mb-6 overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
      >
        <table className="data-table w-full min-w-max border-collapse text-left text-sm">
          <caption className="sr-only">{t('customers.import.mapCaption')}</caption>
          <thead>
            <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
              <th scope="col" className="px-4 py-3 font-semibold">
                {t('customers.import.columnInFile')}
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                {t('customers.import.columnSample')}
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                {t('customers.import.columnMapsTo')}
              </th>
            </tr>
          </thead>
          <tbody>
            {labels.map((label, column) => (
              <tr key={column} className="border-b border-[color:var(--color-line)] last:border-0 align-top">
                <td className="px-4 py-3 font-medium">
                  {label}
                  {guessed.has(column) ? (
                    <span className="badge ml-2 border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                      {t('customers.import.bestGuess')}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-muted)]">
                  {body
                    .slice(0, 3)
                    .map((row) => row.cells[column]?.trim())
                    .filter(Boolean)
                    .join(' · ') || t('customers.import.sampleEmpty')}
                </td>
                <td className="px-4 py-3">
                  <div className="mb-[-1rem] min-w-56">
                    <Select
                      label={t('customers.import.mapsToLabel', { column: label })}
                      value={mapping[column] ?? ''}
                      options={options}
                      onChange={(event) =>
                        onMappingChange(column, (event.target.value || null) as CustomerImportKey | null)
                      }
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>

      <div className="flex flex-wrap gap-3">
        <Button onClick={onBack}>{t('customers.import.chooseDifferentFile')}</Button>
        <Button variant="primary" onClick={onContinue} disabled={mapped === 0}>
          {t('customers.import.continueToReview')}
        </Button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — what will happen
// ---------------------------------------------------------------------------

function ReviewStep({
  preview,
  importable,
  truncated,
  strategy,
  onStrategyChange,
  addProperty,
  onAddPropertyChange,
  checked,
  onCheck,
  progress,
  onBack,
  onImport,
}: {
  preview: readonly PreviewRow[];
  importable: readonly PreviewRow[];
  truncated: boolean;
  strategy: ImportDuplicateStrategy;
  onStrategyChange: (value: ImportDuplicateStrategy) => void;
  addProperty: boolean;
  onAddPropertyChange: (value: boolean) => void;
  checked: CustomerImportResult | null;
  onCheck: () => void;
  progress: { done: number; total: number } | null;
  onBack: () => void;
  onImport: () => void;
}) {
  const { t } = useTranslation();
  const invalid = preview.filter((row) => !row.empty && Object.keys(row.fieldErrors).length > 0);
  const blank = preview.filter((row) => row.empty).length;
  const looseCells = preview.reduce((total, row) => total + row.issues.length, 0);
  const autoChecked = useRef(false);

  useEffect(() => {
    if (autoChecked.current || checked || progress) return;
    if (importable.length === 0 || importable.length > AUTO_CHECK_ROW_LIMIT) return;
    autoChecked.current = true;
    onCheck();
  }, [checked, progress, importable.length, onCheck]);

  const byLine = useMemo(() => new Map((checked?.results ?? []).map((result) => [result.line, result])), [checked]);

  return (
    <>
      <section className="card mb-6">
        <h2 className="mb-3 text-lg font-semibold">{t('customers.import.reviewTitle')}</h2>
        <dl className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label={t('customers.import.statReady')} value={importable.length} />
          <Stat label={t('customers.import.statInvalid')} value={invalid.length} />
          <Stat label={t('customers.import.statBlank')} value={blank} />
          <Stat label={t('customers.import.statLoose')} value={looseCells} />
        </dl>

        {truncated ? (
          <Alert tone="warning" title={t('customers.import.truncatedTitle')}>
            {t('customers.import.truncatedBody', { max: MAX_IMPORT_ROWS })}
          </Alert>
        ) : null}

        {checked ? (
          <Alert tone="info" title={t('customers.import.checkedTitle')}>
            {t('customers.import.checkedBody', {
              create: checked.created,
              update: checked.updated,
              skip: checked.skipped,
              fail: checked.failed,
            })}
          </Alert>
        ) : importable.length > 0 && !progress ? (
          <div className="mb-4">
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('customers.import.checkHint')}</p>
            <Button onClick={onCheck}>{t('customers.import.checkButton')}</Button>
          </div>
        ) : null}

        <Select
          label={t('customers.import.strategyLabel')}
          hint={t('customers.import.strategyHint')}
          value={strategy}
          disabled={Boolean(progress)}
          onChange={(event) => onStrategyChange(event.target.value as ImportDuplicateStrategy)}
          options={[
            { value: 'skip', label: t('customers.import.strategy.skip') },
            { value: 'update', label: t('customers.import.strategy.update') },
            { value: 'create', label: t('customers.import.strategy.create') },
          ]}
        />

        <Checkbox
          label={t('customers.import.addPropertyLabel')}
          hint={t('customers.import.addPropertyHint')}
          checked={addProperty}
          // `send` captured this for the whole loop, so leaving it live would
          // let the control say one thing while the batches still in flight do
          // another.
          disabled={Boolean(progress)}
          onChange={(event) => onAddPropertyChange(event.target.checked)}
        />
      </section>

      {invalid.length > 0 ? (
        <Alert tone="warning" title={t('customers.import.invalidTitle', { count: invalid.length })}>
          {t('customers.import.invalidBody')}
        </Alert>
      ) : null}

      <PreviewTable preview={preview} byLine={byLine} />

      {progress ? (
        <p className="mb-4 text-sm" role="status">
          {t('customers.import.progress', { done: progress.done, total: progress.total })}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button onClick={onBack} disabled={Boolean(progress)}>
          {t('customers.import.backToColumns')}
        </Button>
        <Button
          variant="primary"
          onClick={onImport}
          busy={Boolean(progress)}
          disabled={importable.length === 0 || Boolean(progress)}
        >
          {t('customers.import.importButton', { count: importable.length })}
        </Button>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-sm text-[color:var(--color-muted)]">{label}</dt>
      <dd className="text-2xl font-semibold">{value}</dd>
    </div>
  );
}

/** The first rows as they will be saved, with anything wrong marked on the row. */
function PreviewTable({
  preview,
  byLine,
}: {
  preview: readonly PreviewRow[];
  byLine: ReadonlyMap<number, ImportRowResult>;
}) {
  const { t } = useTranslation();
  const shown = preview.filter((row) => !row.empty).slice(0, 25);
  const columns: CustomerImportKey[] = ['firstName', 'lastName', 'companyName', 'email', 'phone'];

  if (shown.length === 0) return null;

  return (
    <TableScroll
      aria-label={t('customers.import.previewCaption')}
      className="mb-6 overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
    >
      <table className="data-table w-full min-w-max border-collapse text-left text-sm">
        <caption className="sr-only">{t('customers.import.previewCaption')}</caption>
        <thead>
          <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
            <th scope="col" className="px-4 py-3 font-semibold">
              {t('customers.import.columnLine')}
            </th>
            {columns.map((key) => (
              <th key={key} scope="col" className="px-4 py-3 font-semibold">
                {t(`customers.import.field.${key}`)}
              </th>
            ))}
            <th scope="col" className="px-4 py-3 font-semibold">
              {t('customers.import.columnOutcome')}
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const problems = Object.values(row.fieldErrors);
            const result = byLine.get(row.line);
            return (
              <tr key={row.line} className="border-b border-[color:var(--color-line)] last:border-0">
                <td className="px-4 py-3 text-[color:var(--color-muted)]">{row.line}</td>
                {columns.map((key) => (
                  <td key={key} className="px-4 py-3">
                    {String(row.values[key] ?? '')}
                  </td>
                ))}
                <td className="px-4 py-3">
                  {problems.length > 0 ? (
                    <span className="badge border-[color:var(--color-danger)] text-[color:var(--color-danger)]">
                      {problems[0]}
                    </span>
                  ) : result ? (
                    <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                      {t(`customers.import.outcome.${result.status}`)}
                      {result.matched ? ` · ${result.matched.displayName}` : ''}
                    </span>
                  ) : (
                    <span className="text-[color:var(--color-muted)]">{t('customers.import.outcomeUnknown')}</span>
                  )}
                  {row.issues.length > 0 ? (
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {t('customers.import.looseCells', { count: row.issues.length })}
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {preview.filter((row) => !row.empty).length > shown.length ? (
        <p className="px-4 py-3 text-sm text-[color:var(--color-muted)]">
          {t('customers.import.previewTruncated', { shown: shown.length })}
        </p>
      ) : null}
    </TableScroll>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — what happened
// ---------------------------------------------------------------------------

function DoneStep({
  outcome,
  stopped,
  preview,
  labels,
  hasHeader,
  mapping,
  onViewCustomers,
  onImportAnother,
}: {
  outcome: CustomerImportResult;
  /** Rows the run never reached, when a batch failed partway. */
  stopped: number | null;
  preview: readonly PreviewRow[];
  labels: readonly string[];
  /** Whether the source named its own columns — see `downloadProblems`. */
  hasHeader: boolean;
  mapping: ColumnMapping;
  onViewCustomers: () => void;
  onImportAnother: () => void;
}) {
  const { t } = useTranslation();
  const problems = outcome.results.filter((result) => result.status === 'failed');

  return (
    <>
      {stopped !== null ? (
        <Alert tone="warning" title={t('customers.import.stoppedTitle')}>
          {t('customers.import.stoppedBody', { count: stopped })}
        </Alert>
      ) : null}

      <section className="card mb-6">
        <h2 className="mb-3 text-lg font-semibold">
          {stopped === null ? t('customers.import.doneTitle') : t('customers.import.doneStoppedTitle')}
        </h2>
        <dl className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label={t('customers.import.doneCreated')} value={outcome.created} />
          <Stat label={t('customers.import.doneUpdated')} value={outcome.updated} />
          <Stat label={t('customers.import.doneSkipped')} value={outcome.skipped} />
          <Stat label={t('customers.import.doneFailed')} value={outcome.failed} />
        </dl>

        <div className="flex flex-wrap gap-3">
          <Button variant="primary" onClick={onViewCustomers}>
            {t('customers.import.viewCustomers')}
          </Button>
          <Button onClick={onImportAnother}>{t('customers.import.importAnother')}</Button>
          {problems.length > 0 ? (
            <Button onClick={() => downloadProblems(problems, preview, labels, hasHeader, mapping)}>
              {t('customers.import.downloadProblems', { count: problems.length })}
            </Button>
          ) : null}
        </div>
      </section>

      {problems.length > 0 ? (
        <TableScroll
          aria-label={t('customers.import.problemsCaption')}
          className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
        >
          <table className="data-table w-full min-w-max border-collapse text-left text-sm">
            <caption className="sr-only">{t('customers.import.problemsCaption')}</caption>
            <thead>
              <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('customers.import.columnLine')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('customers.import.columnProblem')}
                </th>
              </tr>
            </thead>
            <tbody>
              {problems.slice(0, 100).map((result) => (
                <tr key={result.line} className="border-b border-[color:var(--color-line)] last:border-0">
                  <td className="px-4 py-3 text-[color:var(--color-muted)]">{result.line}</td>
                  <td className="px-4 py-3">{result.message ?? t(`customers.import.reason.${result.reason}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}
    </>
  );
}

/**
 * The rows that did not import, as a file to fix and feed back in.
 *
 * Their original cells, untouched, with the reason appended — so the correction
 * happens in the spreadsheet the person already knows, and the fixed file goes
 * straight back through this same screen.
 */
function downloadProblems(
  problems: readonly ImportRowResult[],
  preview: readonly PreviewRow[],
  labels: readonly string[],
  hasHeader: boolean,
  mapping: ColumnMapping,
): void {
  // A file with no header row of its own has only synthetic names — "A", "B" —
  // and a correction file headed with those reads its own header back as a
  // customer called A. The mapped columns are written under their field names
  // instead, which the matcher recognizes, so the corrected file comes back in
  // with its header understood and its mapping already right.
  const header = [
    ...labels.map((label, column) => {
      if (hasHeader) return label;
      const key = mapping[column];
      return key ? customerImportField(key).label : label;
    }),
    'Import problem',
  ].map(quoteCsv);
  const rows = problemRows(problems, preview, labels).map((row) => row.map(quoteCsv).join(','));

  downloadFile(
    'customer-import-problems.csv',
    'text/csv;charset=utf-8',
    `\ufeff${[header.join(','), ...rows].join('\r\n')}\r\n`,
  );
}

/** RFC 4180 quoting, plus the leading-apostrophe guard a spreadsheet needs. */
function quoteCsv(value: string): string {
  const guarded = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replaceAll('"', '""')}"` : guarded;
}

function downloadFile(filename: string, type: string, body: string): void {
  const url = URL.createObjectURL(new Blob([body], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Corrects a rehearsal for households the file lists more than once.
 *
 * Each dry-run request writes nothing, so the next one cannot see what the last
 * one said it would create — which means two rows for the same person on
 * opposite sides of a batch boundary both come back "new", while the real
 * import creates the first and matches the second against it. The divergence
 * lands on exactly the large migrations batching exists for, so the browser,
 * which is the only party holding the whole file, reconciles it here.
 *
 * Only rows the server called `created` are reconsidered: one it already
 * matched against the database, or against an earlier row of the same batch,
 * has been accounted for.
 */
function withInFileDuplicates(
  result: CustomerImportResult,
  rows: readonly PreviewRow[],
  strategy: ImportDuplicateStrategy,
  countryCode: string,
): CustomerImportResult {
  if (strategy === 'create') return result;

  const byLine = new Map(rows.map((row) => [row.line, contactOf(row, countryCode)]));
  const seen: Contact[] = [];
  const adjusted = { ...result, results: [...result.results] };

  for (const [index, entry] of adjusted.results.entries()) {
    const contact = byLine.get(entry.line);
    if (!contact) continue;

    // An update counts too. A row naming a customer by id can put a new address
    // on them, and the real import would then match a later row carrying that
    // address; the rehearsal has to see the same thing.
    if (entry.status !== 'created') {
      if (entry.status === 'updated') seen.push(contact);
      continue;
    }

    const earlier = seen.find((other) => sameContact(other, contact, countryCode));
    if (!earlier) {
      seen.push(contact);
      continue;
    }

    adjusted.created -= 1;
    if (strategy === 'update') {
      adjusted.updated += 1;
      adjusted.results[index] = { ...entry, status: 'updated' };
    } else {
      adjusted.skipped += 1;
      adjusted.results[index] = { ...entry, status: 'skipped', reason: 'duplicate' };
    }
  }

  return adjusted;
}

interface Contact {
  email: string | null;
  phone: string | null;
}

function contactOf(row: PreviewRow, countryCode: string): Contact {
  const email = typeof row.values.email === 'string' ? row.values.email.trim().toLowerCase() : null;
  const phone = typeof row.values.phone === 'string' ? row.values.phone : null;
  return { email: email || null, phone: normalizePhoneToE164(phone, countryCode) };
}

/** The same test the server uses: an exact email, or the same phone number. */
function sameContact(a: Contact, b: Contact, countryCode: string): boolean {
  if (a.email && b.email && a.email === b.email) return true;
  if (a.phone && b.phone && isSamePhoneNumber(a.phone, b.phone, countryCode)) return true;
  return false;
}

/**
 * Folds the rows the browser refused into the server's answer.
 *
 * They never reached the server, so its counts cannot include them — and a
 * summary that quietly leaves out the twelve rows somebody most needs to see is
 * the one thing this screen must not do.
 */
function mergeLocal(result: CustomerImportResult, local: readonly ImportRowResult[]): CustomerImportResult {
  const failed = local.filter((row) => row.status === 'failed').length;
  const skipped = local.length - failed;
  return {
    ...result,
    failed: result.failed + failed,
    skipped: result.skipped + skipped,
    results: [...result.results, ...local].sort((a, b) => a.line - b.line),
  };
}
